import * as z from "zod";
import { userFacingError } from "../authorization";
import { isUniqueConstraintViolation } from "../dbErrors";
import { zql } from "../generatedDrizzleSchema";
import {
  grantPrincipalResolve,
  grantRoleResolve,
} from "../rbac/grantResources";
import { BUILTIN_ROLE_IDS, type BuiltinRoleId } from "../rbac/roles";
import {
  assertValidSpaceDefaultJoinRole,
  grantEntry,
  insertGrantsDeduped,
  validateGrants,
} from "../rbac/validation";
import { assertValidSlug, slugifyName, slugValidationError } from "../slug";
import { USER_ASSIGNABLE_SPACE_MODES } from "../spaceMode";
import {
  assertNameAvailable,
  assertSlugAvailable,
  spaceNameField,
  spaceSlugField,
} from "../spaceName";
import { mutator } from "./mutator";

export const createSpace = mutator({
  schema: z.object({
    name: spaceNameField,
    id: z.string(),
    slug: spaceSlugField.optional(),
    mode: z.enum(USER_ASSIGNABLE_SPACE_MODES),
    grants: z.array(grantEntry).optional(),
    defaultJoinBuiltinRoleId: z.enum(BUILTIN_ROLE_IDS).nullable().optional(),
    defaultJoinCustomRoleId: z.string().nullable().optional(),
  }),
  resolve: {
    ...grantPrincipalResolve,
    ...grantRoleResolve,
    defaultJoinCustomRole: {
      kind: "customRole",
      from: "defaultJoinCustomRoleId",
      optional: true,
    },
  },
  permissions: [
    { permission: "tenant:space_create", scopeId: ({ ctx }) => ctx.tenantId },
  ],
  fn: async ({
    tx,
    ctx,
    args: {
      name,
      id,
      slug,
      mode,
      grants,
      defaultJoinBuiltinRoleId,
      defaultJoinCustomRoleId,
      defaultJoinCustomRole,
      grantCustomRoles,
    },
  }) => {
    if (defaultJoinBuiltinRoleId && defaultJoinCustomRoleId) {
      throw userFacingError(
        "A space can have only one default join role at a time"
      );
    }
    assertValidSpaceDefaultJoinRole({
      builtinRoleId: defaultJoinBuiltinRoleId as BuiltinRoleId | null,
      customRole: defaultJoinCustomRole,
    });

    if (grants) {
      validateGrants(grants, "space", grantCustomRoles);
    }

    const existing = await tx.run(zql.spaces.where("tenantId", ctx.tenantId));
    assertNameAvailable(existing, name);

    const resolvedSlug = slug ?? slugifyName(name);
    const slugError = slugValidationError(resolvedSlug);
    if (slugError) {
      throw userFacingError(
        slug === undefined
          ? "This name would produce an invalid space URL. Set a custom space URL instead."
          : slugError
      );
    }
    assertValidSlug(resolvedSlug);
    if (slug !== undefined) assertSlugAvailable(existing, resolvedSlug);

    const now = Date.now();
    try {
      await tx.mutate.spaces.insert({
        id,
        name,
        slug: resolvedSlug,
        mode,
        createdAt: now,
        updatedAt: now,
        defaultJoinBuiltinRoleId:
          (defaultJoinBuiltinRoleId as BuiltinRoleId | null) ?? undefined,
        defaultJoinCustomRoleId: defaultJoinCustomRoleId ?? undefined,
        tenantId: ctx.tenantId,
      });
    } catch (err) {
      if (isUniqueConstraintViolation(err, "spaces_tenant_name_unique")) {
        throw userFacingError("A space with this name already exists");
      }
      if (isUniqueConstraintViolation(err, "spaces_tenant_slug_unique")) {
        throw userFacingError(
          slug
            ? "This space URL is already taken"
            : "A space with a similar name already exists"
        );
      }
      throw err;
    }

    if (grants) {
      await insertGrantsDeduped(tx, grants, id, ctx.tenantId);
    }
  },
});
